import type { TimingWindowsConfig } from '@/types';

/** Logical internal resolution. All gameplay math happens in this space; Phaser's
 * ScaleManager (FIT) scales it to the actual device viewport so coordinates never
 * change unpredictably between devices. */
export const INTERNAL_WIDTH = 720;
export const INTERNAL_HEIGHT = 1280;

export const LANE_WIDTH = INTERNAL_WIDTH / 2;

/** Shifts the catch line and cats up off the very bottom edge, keeping their
 * relative spacing unchanged, so a player's thumbs (holding the device from
 * underneath, or touching near the bottom edge) don't cover the cats. */
const CAT_AREA_LIFT = INTERNAL_HEIGHT * 0.1;

/** Y position (in internal px) of the line notes are judged against. */
export const CATCH_LINE_Y = INTERNAL_HEIGHT - 210 - CAT_AREA_LIFT;

/** Y position where notes are first spawned/visible. */
export const SPAWN_Y = -60;

/** Y position of the cats' resting center. */
export const CAT_Y = INTERNAL_HEIGHT - 150 - CAT_AREA_LIFT;

/** How long, in seconds, a note takes to fall from spawn to the catch line by default. */
export const DEFAULT_NOTE_TRAVEL_TIME = 1.8;

/** Timing windows in milliseconds, centered on a note's target time. These gate
 * whether a note is a hit or a miss at all -- NOT the Perfect/Good split (see
 * PERFECT_POSITION_FRACTION below for that). `perfectMs` is unused now but kept for
 * config-shape stability / possible future use; tune goodMs/missGraceMs here. */
export const TIMING_WINDOWS: TimingWindowsConfig = {
  perfectMs: 70,
  goodMs: 160,
  missGraceMs: 160,
};

/** Half-width, in normalized lane units (0..1), of a cat's mouth/catch hitbox. */
export const CAT_CATCH_HALF_WIDTH = 0.16;

/** Half-width, in internal px, of a food note's collision footprint. Scaled with
 * FOOD_DISPLAY_SIZE in FoodNote.ts so the catch tolerance keeps tracking the sprite's
 * visual size rather than drifting out of sync with it. */
export const FOOD_HITBOX_HALF_WIDTH_PX = 30;

/** Fraction of the full catch tolerance (cat half-width + food half-width) within
 * which a catch counts as Perfect rather than Good. Grading is based on how
 * centered the catch is, not on timing precision: once a note is reachable at all
 * (within TIMING_WINDOWS), a player who is continuously in position will always
 * end up catching it almost exactly on the beat -- timing alone can't distinguish
 * skill for a hold-position mechanic like this one, but how precisely you're
 * centered on the target can, and it's something the player controls on every
 * single note regardless of when they arrived. */
export const PERFECT_POSITION_FRACTION = 0.4;

/** Half-width, in normalized lane units (0..1), of the cat's visible body for movement clamping. */
export const CAT_BODY_HALF_WIDTH = 0.14;

/** Rendered size (width and height, cats' art is square) of the cat sprite in internal px. */
export const CAT_DISPLAY_SIZE = 190;

/** Vertical offset (internal px, negative = up) from the cat sprite's center to its
 * mouth, used for the catch hitbox. Calibrated against the generated art's face
 * position; re-tune here if the art style changes. */
export const CAT_MOUTH_OFFSET_Y = -16;

/** Cat horizontal movement smoothing (higher = snappier). Used as an exponential approach rate. */
export const CAT_MOVE_SMOOTHING = 18;

/** Keyboard-driven cat movement speed in normalized lane units per second. */
export const CAT_KEYBOARD_SPEED = 1.6;

export const SCORE_POINTS: Record<'perfect' | 'good' | 'miss', number> = {
  perfect: 100,
  good: 60,
  miss: 0,
};

/** Combo count thresholds that grant a score multiplier bonus. */
export const COMBO_MULTIPLIER_THRESHOLDS: { combo: number; multiplier: number }[] = [
  { combo: 100, multiplier: 2.0 },
  { combo: 50, multiplier: 1.5 },
  { combo: 25, multiplier: 1.25 },
  { combo: 10, multiplier: 1.1 },
];

/** Global audio calibration offset in milliseconds. Positive values delay the perceived
 * audio relative to visuals/hit windows (i.e. compensates for output latency).
 * Overridden per-user by GameSettings.audioOffsetMs (see SaveManager). */
export const DEFAULT_AUDIO_OFFSET_MS = 0;

export const STORAGE_KEY = 'meow-melody-save-v1';

export const DEBUG_QUERY_PARAM = 'debug';
